import * as cron from "node-cron";
import type {
  CreateSchedulerOptions,
  FailedJob,
  ScheduleOptions,
  ScheduledJobRecord,
  Scheduler,
} from "@gencow/core";

type ActionHandler = (args: unknown) => Promise<unknown>;

export interface CronInfoEntry {
  name: string;
  pattern: string;
  registeredAt: string;
}

export interface PendingJobEntry {
  id: string;
  action: string;
  scheduledAt: string;
  status?: "pending" | "running" | "completed" | "failed";
}

export interface SchedulerInfo {
  crons: CronInfoEntry[];
  pendingJobs: PendingJobEntry[];
  failedJobs: FailedJob[];
}

export interface RuntimeScheduler extends Scheduler {
  getSchedulerInfo(): SchedulerInfo;
}

const MAX_FAILED_JOBS = 100;

export function createScheduler(options?: CreateSchedulerOptions): RuntimeScheduler {
  const timers = new Map<string, NodeJS.Timeout>();
  const isDurable = !!(options?.persistJob && options?.removeJob);
  const cronJobs = new Map<string, cron.ScheduledTask>();
  const actions = new Map<string, ActionHandler>();
  const cronInfo: CronInfoEntry[] = [];
  const pendingJobs: PendingJobEntry[] = [];
  const failedJobs: FailedJob[] = [];

  let jobCounter = 0;

  function generateId(): string {
    return `job_${++jobCounter}_${Date.now()}`;
  }

  function recordFailure(id: string, action: string, args: unknown, error: Error): void {
    failedJobs.push({
      id,
      action,
      args,
      error: error.message,
      failedAt: new Date().toISOString(),
    });
    while (failedJobs.length > MAX_FAILED_JOBS) {
      failedJobs.shift();
    }
  }

  async function runRegisteredAction(action: string, args: unknown): Promise<void> {
    const handler = actions.get(action);
    if (!handler) {
      console.error(`[scheduler] Action "${action}" not registered`);
      throw new Error(`Action "${action}" not registered`);
    }
    await handler(args);
    console.log(`[scheduler] Action "${action}" completed`);
  }

  function scheduleInMemory(
    id: string,
    ms: number,
    action: string,
    args: unknown,
    scheduleOpts: ScheduleOptions | undefined,
    jobEntry: PendingJobEntry,
  ): void {
    const timer = setTimeout(async () => {
      jobEntry.status = "running";
      try {
        await runRegisteredAction(action, args);
        jobEntry.status = "completed";
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        jobEntry.status = "failed";
        console.error(`[scheduler] Action "${action}" failed (job: ${id}):`, err.message);
        recordFailure(id, action, args, err);

        if (scheduleOpts?.onError) {
          try {
            await runRegisteredAction(scheduleOpts.onError, {
              failedAction: action,
              failedJobId: id,
              error: err.message,
              originalArgs: args,
            });
            console.log(`[scheduler] onError handler "${scheduleOpts.onError}" completed for "${action}"`);
          } catch (onErrorErr) {
            console.error(`[scheduler] onError handler "${scheduleOpts.onError}" also failed:`, onErrorErr);
          }
        }
      } finally {
        timers.delete(id);
        setTimeout(() => {
          const idx = pendingJobs.findIndex((job) => job.id === id);
          if (idx >= 0) pendingJobs.splice(idx, 1);
        }, 60_000);
      }
    }, ms);
    timers.set(id, timer);
    console.log(
      `[scheduler] Scheduled "${action}" to run after ${ms}ms (id: ${id})${scheduleOpts?.onError ? ` [onError: ${scheduleOpts.onError}]` : ""}`,
    );
  }

  return {
    runAfter(ms: number, action: string, args?: unknown, scheduleOpts?: ScheduleOptions): string {
      const id = scheduleOpts?.id ?? generateId();
      const jobEntry: PendingJobEntry = {
        id,
        action,
        scheduledAt: new Date().toISOString(),
        status: "pending",
      };
      pendingJobs.push(jobEntry);

      if (isDurable) {
        const runAt = new Date(Date.now() + ms);
        const job: ScheduledJobRecord = {
          id,
          action,
          args,
          runAt,
          onErrorAction: scheduleOpts?.onError,
          dedupeKey: scheduleOpts?.dedupeKey,
        };
        options.persistJob!(job)
          .then(() => {
            console.log(
              `[scheduler] Persisted "${action}" to run at ${runAt.toISOString()} (id: ${id}, durable)` +
                `${scheduleOpts?.onError ? ` [onError: ${scheduleOpts.onError}]` : ""}`,
            );
          })
          .catch((err) => {
            console.error(
              `[scheduler] Failed to persist job ${id}:`,
              err instanceof Error ? err.message : err,
            );
            if (options.persistFailureMode === "fail-closed") {
              jobEntry.status = "failed";
              recordFailure(id, action, args, err instanceof Error ? err : new Error(String(err)));
              return;
            }
            scheduleInMemory(id, ms, action, args, scheduleOpts, jobEntry);
          });
        return id;
      }

      scheduleInMemory(id, ms, action, args, scheduleOpts, jobEntry);
      return id;
    },

    runAt(timestamp: number | Date, action: string, args?: unknown, options?: ScheduleOptions): string {
      const target = timestamp instanceof Date ? timestamp.getTime() : timestamp;
      const ms = Math.max(0, target - Date.now());
      return this.runAfter(ms, action, args, options);
    },

    cancel(jobId: string): boolean {
      if (isDurable) {
        const idx = pendingJobs.findIndex((job) => job.id === jobId);
        if (idx >= 0) {
          pendingJobs.splice(idx, 1);
          options.removeJob!(jobId).catch((err) => {
            console.error(
              `[scheduler] Failed to remove persisted job ${jobId}:`,
              err instanceof Error ? err.message : err,
            );
          });
          console.log(`[scheduler] Cancelled job ${jobId} (durable)`);
          return true;
        }
        options.removeJob!(jobId).catch(() => {});
        return false;
      }

      const timer = timers.get(jobId);
      if (timer) {
        clearTimeout(timer);
        timers.delete(jobId);
        const idx = pendingJobs.findIndex((job) => job.id === jobId);
        if (idx >= 0) pendingJobs.splice(idx, 1);
        console.log(`[scheduler] Cancelled job ${jobId}`);
        return true;
      }
      return false;
    },

    cron(name: string, pattern: string, handler: () => Promise<void>): void {
      if (cronJobs.has(name)) {
        cronJobs.get(name)!.stop();
      }
      const task = cron.schedule(pattern, async () => {
        console.log(`[scheduler] Cron "${name}" triggered`);
        try {
          await handler();
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          console.error(`[scheduler] Cron "${name}" failed:`, err.message);
          recordFailure(`cron_${name}_${Date.now()}`, `cron:${name}`, {}, err);
        }
      });
      cronJobs.set(name, task);
      const infoEntry = { name, pattern, registeredAt: new Date().toISOString() };
      const existingInfoIndex = cronInfo.findIndex((entry) => entry.name === name);
      if (existingInfoIndex >= 0) {
        cronInfo[existingInfoIndex] = infoEntry;
      } else {
        cronInfo.push(infoEntry);
      }
      console.log(`[scheduler] Registered cron "${name}" with pattern "${pattern}"`);
    },

    registerAction(name: string, handler: ActionHandler): void {
      actions.set(name, handler);
    },

    async executeActionStrict(name: string, args?: unknown): Promise<void> {
      await runRegisteredAction(name, args);
    },

    async executeAction(name: string, args?: unknown): Promise<void> {
      try {
        await runRegisteredAction(name, args);
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error(`[scheduler] executeAction("${name}") failed: ${msg}`);
      }
    },

    getSchedulerInfo(): SchedulerInfo {
      return {
        crons: [...cronInfo],
        pendingJobs: [...pendingJobs],
        failedJobs: [...failedJobs],
      };
    },
  };
}
